<template>
    <b-container fluid class="print-paper-choice">
        <b-row>
            <b-col cols="6" md="3">
                <h4>{{$t('printComponent.dashboard.papertype')}}</h4>
                <print-settings-form
                    ref="settingsForm"
                    v-if="formLayout && formLayout.formatOptions"
                    :key="`formatOptions${componentUpdateIndex}`"
                    :formData="formLayout.formatOptions"
                    @update="$_updatedForm($event)"
                />
            </b-col>
            <b-col cols="6" md="3">
                <h4>{{$t('printComponent.dashboard.margins')}}</h4>
                <print-settings-form
                    ref="marginsForm"
                    v-if="formLayout && formLayout.margins"
                    :key="`margins${componentUpdateIndex}`"
                    :formData="formLayout.margins"
                    @update="$_updatedForm($event)"
                />
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
    import PrintSettingsForm from '../PrintSettingsForm.vue';

    const CUSTOMFORMAT = 'Aangepast';
    const DEFAULTMARGIN = 10;
    export default {
        props: {
            mode: { type: String, required: false, default: 'edit' },
            format: { type: Object, required: true }
        },
        components: { 'print-settings-form': PrintSettingsForm },
        data() {
            return {
                formats: [
                    { name: 'A0', size: [1189, 841] },
                    { name: 'A1', size: [841, 594] },
                    { name: 'A2', size: [594, 420] },
                    { name: 'A3', size: [420, 297] },
                    { name: 'A4', size: [297, 210] },
                    { name: 'A5', size: [210, 148] }
                ],
                formFields: null,
                componentUpdateIndex: 0
            };
        },

        async created() {
            this.init();
        },

        methods: {
            init () {
                // prepare the choices for formats and orientation
                const formatOptions = this.formats
                    .map(formatListItem => (
                        {
                            text: formatListItem.name,
                            value: formatListItem.name.toUpperCase()
                        }
                    ));
                formatOptions.push({ text: CUSTOMFORMAT, value: CUSTOMFORMAT });

                const orientationOptions = [
                    { text: 'staand', value: 'portrait' },
                    { text: 'liggend', value: 'landscape' },
                ];

                // prepare formfields
                const format = this.$_createPaperFormObject('format', 'select', 'format', { options: formatOptions });
                const title = this.$_createPaperFormObject('title', 'text', 'title', { maxlength: 32, title: 'title' });
                const description = this.$_createPaperFormObject('description', 'text', 'description', { maxlength: 512, title: 'description', required: false });
                const orientation = this.$_createPaperFormObject('orientation', 'radio', 'orientation', { options: orientationOptions });
                const width = this.$_createPaperFormObject('width', 'number', 'width', { maxlength: 4, title: 'width', type: 'number', disabled: true, required: false });
                const height = this.$_createPaperFormObject('height', 'number', 'height', { maxlength: 4, title: 'height', type: 'number', disabled: true, required: false });

                const marginTop = this.$_createPaperFormObject('marginTop', 'number', 'marginTop', {
                    maxlength: 4,
                    title: 'marginTop',
                    value: this.format.margins.top || DEFAULTMARGIN,
                    type: 'number',
                    disabled: false
                });

                const marginRight = this.$_createPaperFormObject('marginRight', 'number', 'marginRight', {
                    maxlength: 4,
                    title: 'marginRight',
                    value: this.format.margins.right || DEFAULTMARGIN,
                    type: 'number',
                    disabled: false
                });

                const marginBottom = this.$_createPaperFormObject('marginBottom', 'number', 'marginBottom', {
                    maxlength: 4,
                    title: 'marginBottom',
                    value: this.format.margins.bottom || DEFAULTMARGIN,
                    type: 'number',
                    disabled: false
                });

                const marginLeft = this.$_createPaperFormObject('marginLeft', 'number', 'marginLeft', {
                    maxlength: 4,
                    title: 'marginLeft',
                    value: this.format.margins.left || DEFAULTMARGIN,
                    type: 'number',
                    disabled: false
                });
                // add fields to an object for the form
                this.formFields = {
                    format,
                    title,
                    description,
                    orientation,
                    width,
                    height,
                    marginTop,
                    marginRight,
                    marginBottom,
                    marginLeft,
                };
            },
            $_createPaperFormObject(name, type, id, settings) {
                if (!this.format) {
                    // eject when there is no format selected
                    return undefined;
                }
                // add the default fields for a formfield
                let formConfig = {
                    id: id,
                    name: id,
                    text: this.$t(`printComponent.dashboard.${name}`),
                    type,
                    required: typeof settings.required === 'boolean' ? settings.required : true,
                    value: settings.value || this.format[id],
                    valid: null,
                    disabled: settings.disabled
                };
                // add the fields specific for a type of formfield
                if (type === 'text' || type === 'textarea'){
                    formConfig = {
                        ...formConfig,
                        placeholder: id,
                        invalidMessage: this.$t('printComponent.dashboard.invalidInput'),
                        maxlength: settings.maxLength,
                        headerText: settings.title,
                        fieldType: settings.type
                    };
                } else if (type==='select' || type==='radio') {
                    formConfig = {
                        ...formConfig,
                        invalidMessage: this.$t('printComponent.dashboard.invalidInput'),
                        options: settings.options
                    };
                }
                return formConfig;
            },
            $_updatedForm(data){
                if (data.format){
                    // if available fill the width and height with chosen values
                    if (data.format !== CUSTOMFORMAT) {
                        const chosenFormat = this.formats.find(({ name }) => name.toUpperCase() === data.format);
                        data.width = data.orientation === 'portrait' ? chosenFormat.size[1] : chosenFormat.size[0];
                        data.height = data.orientation === 'portrait' ? chosenFormat.size[0] : chosenFormat.size[1];
                    }
                    // only make the size editable for a custom format
                    this.formFields.width.disabled = data.format !== CUSTOMFORMAT;
                    this.formFields.height.disabled = data.format !== CUSTOMFORMAT;
                    this.formFields.width.required = data.format === CUSTOMFORMAT;
                    this.formFields.height.required = data.format === CUSTOMFORMAT;
                }
                // copy the values from the updated form to the local formfield data
                Object.keys(data).forEach(field => {
                    this.formFields[field].value = data[field];
                });
                // emit the new data to the listeners
                this.$_updatedValue();
            },
            $_updatedValue() {
                // prepare the object for the values
                const exposedValues = { margins: {} };
                // add the values from the formfields
                // make an exception for margins, wich are collected in a child object
                Object.keys(this.formFields)
                    .forEach(field => {
                        if (field.startsWith('margin')) {
                            exposedValues.margins[field.replace('margin', '').toLowerCase()] = parseInt(this.formFields[field].value, 10);
                        } else {
                            exposedValues[field] = this.formFields[field].value;
                        }
                    });
                // expose the values to the listeners
                this.$emit('settings', exposedValues);
            },
            validate() {
                this.$refs.settingsForm.validate();
                this.$refs.marginsForm.validate();
            }
        },

        computed: {
            formLayout: function() {
                if (!this.formFields) {
                    return undefined;
                }
                // create data for two forms
                return  {
                    formatOptions: [
                        this.formFields.format,
                        this.formFields.title,
                        this.formFields.description,
                        this.formFields.format.value !== CUSTOMFORMAT ? this.formFields.orientation : undefined,
                        this.formFields.format.value === CUSTOMFORMAT ? this.formFields.width : undefined,
                        this.formFields.format.value === CUSTOMFORMAT ? this.formFields.height : undefined
                    ].filter(field => field !== undefined),
                    margins: [
                        this.formFields.marginTop,
                        this.formFields.marginRight,
                        this.formFields.marginBottom,
                        this.formFields.marginLeft,
                    ]
                };
            },
            valid: function() {
                return this.$refs.settingsForm.valid && this.$refs.marginsForm.valid;
            }
        }

    };
</script>
